import React from 'react';
import { Target, Heart, Users, Leaf, Award, TrendingUp, Shield, Lightbulb } from 'lucide-react';

const Mission = () => {
  const values = [
    {
      icon: Shield,
      title: "Trust",
      description: "It is the main pillar for the success of our company and shaping our relationships with customers, suppliers, employees and other stakeholders. We deliver our promises every time."
    },
    {
      icon: Award,
      title: "Quality",
      description: "It is the foundation of our brand and key for keeping our customers satisfied for years. Our hygienic production process, 100% compliance on food related statutory requirements, constant quality control and hi-tech lab help us in producing world class quality products."
    },
    {
      icon: Heart,
      title: "Integrity",
      description: "Our strong moral principles, honesty and transparency towards our process and work is the core value of the company. Creating faith in our customers is inculcated in every employee from day one."
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "To stay ahead of the curve and grow our company with the market we are always focused on continuous innovation and technological advancements, innovation in product development, packaging and distribution."
    },
    {
      icon: Users,
      title: "Customer Satisfaction",
      description: "We are committed to deliver high customer satisfaction with continuous improvement in our services and product quality that also help us in building long term relationships with our customers."
    },
    {
      icon: Target,
      title: "Pride",
      description: "We feel proud of our brand, our products, our team and the company we work for. We care for every stakeholder—farmers, consumers, and the supply chain—all nourished equally."
    },
    {
      icon: TrendingUp,
      title: "Exceeding Expectations",
      description: "Anticipate the needs of those we serve, and deliver products and services that meet and exceed the needs and expectations of our customers."
    },
    {
      icon: Leaf,
      title: "Benchmarking",
      description: "We have established a benchmark against which we measure our product's quality. This serves as a reference point to ensure that the final product meets our expectations through regular testing and monitoring."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-rose-500 to-red-500 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">Our Mission, Vision & Values</h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Bringing the goodness of pure, traditional dairy products to every home
          </p>
        </div>
      </div>

      {/* Vision & Mission Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Vision Section - Full Width */}
        <div className="mb-12">
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-10 shadow-lg border border-rose-100">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center mr-4">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-rose-900">Our Vision</h2>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed">
              To procure, produce and provide world class quality products at affordable prices and become a brand of choice in every market and segment we operate in.
            </p>
          </div>
        </div>

        {/* Mission Section with Image - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Image on Left */}
          <div className="flex items-center justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_f5e3540e-97a9-470e-8a92-ab500e1eb8c7/artifacts/cshpbvv8_vlcsnap-2022-01-01-16h11m22s204-2.png"
              alt="Arihant Dairy Production"
              className="rounded-2xl shadow-xl w-full h-auto object-contain"
            />
          </div>

          {/* Mission Section on Right */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-3xl p-10 shadow-lg border border-rose-100">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-rose-600 rounded-full flex items-center justify-center mr-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl font-bold text-rose-900">Our Mission</h2>
            </div>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                Achieve growth and continual improvement through breakthrough innovation and teamwork without compromising on our traditional values and values of quality, trust and integrity.
              </p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">•</span>
                  <span>Procure best quality milk, produce nutritious and healthy products, maintain consistency and promptness in delivery of products and services.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">•</span>
                  <span>Encourage our team by providing a healthy working environment along with continuous upgradation and innovation in technology and ensuring a fair return to the company.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">•</span>
                  <span>Offering world class quality products at reasonable prices.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-rose-600 mr-3 mt-1">•</span>
                  <span>Building the foundation of health and wellness for a happy society.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className="text-4xl font-bold text-rose-900 text-center mb-12">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-7 w-7 text-rose-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-rose-900 mb-3 text-center">{value.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed text-center">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-rose-500 to-red-500 rounded-2xl p-12 text-white">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold mb-2">35+</div>
              <div className="text-white/90">Years of Experience</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">1 Crore+</div>
              <div className="text-white/90">Happy Customers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">20,000+</div>
              <div className="text-white/90">Partner Farmers</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">20+</div>
              <div className="text-white/90">Premium Dairy Products</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mission;
